/**
 * WIE VIELE GRENZPUNKTE STEHEN IM AUSSCHNITT - UND AB WANN STEHEN SIE DA.
 *
 * ORIGINALTEXT MARVIN (05.09.2026, mit dem Bildschirmfoto der Stufe 20)
 *   „bei den grenzen fehlen die grenzpunkte."
 *
 * **DER DECKEL HAT GEZÄHLT, WAS NICHTS KOSTET.** Bis .111 hiess er
 * `GRENZPUNKTE_BIS_STUECKE = 80` - eine Grenze auf die Zahl der FLURSTÜCKE,
 * mit der Rechnung „zweihundertvierzig Stück mal einem Dutzend Ecken sind
 * dreitausend Kreise". Auf seinem Bildschirm standen **108 Flurstücke**, also
 * kein einziger Grenzpunkt; die Karte sah aus, als sei die Zeichnung kaputt,
 * und der Deckel sagte kein Wort dazu.
 *
 * **EIN FLURSTÜCK HAT NICHT EIN DUTZEND ECKEN, SONDERN VIER ODER
 * ZWEIHUNDERT.** Ein Reihenhausgrundstück ist ein Rechteck, ein Waldstück am
 * Hang hat einen Umring aus hundert Punkten. Die Zahl der Stücke ist ein
 * STELLVERTRETER für die Zahl der Kreise, und ein Stellvertreter liegt genau
 * dann daneben, wenn es darauf ankommt. Gezählt wird deshalb, was WIRKLICH
 * gezeichnet wird.
 *
 * DIESE DATEI TRÄGT KEIN JSX, und das ist Absicht: eine Datei mit JSX lässt
 * sich mit `--experimental-strip-types` nicht laden, und dann bliebe einem
 * Prüfstand nur die Textsuche - **eine Textsuche misst, dass etwas dasteht,
 * nicht, dass es stimmt.** Siebte Datei dieser Art nach `namen.ts`,
 * `terminraster.ts`, `klebeband.ts`, `kartenmass.ts`, `oberflaeche.ts` und
 * `kartenebene.ts`.
 */

/**
 * Ab dieser Stufe stehen die GRENZPUNKTE einzeln da.
 *
 * Darunter liegen zwei Punkte derselben Grenze weniger als zwei Bildpunkte
 * auseinander; dann ist die Reihe der Kreise keine Auskunft mehr, sondern
 * eine dicke Linie.
 */
export const GRENZPUNKTE_AB_STUFE = 18;

/**
 * Und höchstens so viele Grenzpunkte stehen im Ausschnitt.
 *
 * ORIGINALTEXT MARVIN (05.09.2026, Bildschirmfoto der Stufe 19: „219
 * Flurstücke, 2427 Grenzpunkte - zu dicht, näher heranzoomen")
 *   „die grenzpunkte sind immernoch nicht da"
 *
 * **ZUM ZWEITEN MAL DERSELBE FEHLER, NUR MIT EINER ANDEREN ZAHL.** Der
 * Deckel stand erst auf achtzig FLURSTÜCKEN, dann auf zweitausend PUNKTEN -
 * und beide Male hat er bei dem Ausschnitt gegriffen, den Marvin wirklich
 * vor sich hat. Zweitausend war an seinem VORIGEN Bildschirmfoto gemessen
 * (108 Flurstücke, rund tausend Ecken); sein nächstes trug 2427.
 *
 * > **Eine Schwelle, die am letzten Bildschirmfoto gemessen ist, ist an dem
 * > Tag falsch, an dem jemand einen Ausschnitt weiter klickt.**
 *
 * UND DER GRUND FÜR DEN DECKEL IST WEGGEFALLEN. Er schützte davor,
 * zweitausend einzelne Kreise in die Zeichnung zu hängen. Seit .113 stehen
 * alle Punkte eines Flurstücks in ZWEI Pfaden - ein Wegstück der Länge null
 * mit runder Kappe ist ein Kreis -, und damit kostet der tausendste Punkt so
 * viel wie der erste. **Ein Grund, der weggefallen ist, trägt eine
 * Entscheidung nicht weiter.**
 *
 * Zwanzigtausend ist deshalb kein gemessener Wert mehr, sondern ein Riegel
 * gegen einen Ausschnitt, den niemand sehen will: bei so vielen Punkten
 * stehen ohnehin schon tausend Flurstücke auf dem Bild, und die Grenzlinien
 * darunter sind längst eine Fläche. Er liegt fast eine Zehnerpotenz über dem
 * Alltag - **ein Deckel, der im Alltag greift, ist keine Vorsichtsmassnahme,
 * sondern ein Fehler.**
 */
export const GRENZPUNKTE_BIS_PUNKTE = 20000;

/** Ein Flurstück, so weit es diese Rechnung angeht. */
export interface MitUmring {
  geometry?: {
    type?: string;
    coordinates?: any;
  } | null;
}

/**
 * Wie viele Grenzpunkte trägt dieser Ausschnitt?
 *
 * **DER LETZTE PUNKT EINES UMRINGS IST DER ERSTE.** GeoJSON schliesst den
 * Ring, indem es die Anfangsecke am Ende wiederholt; hier wird sie genauso
 * abgezogen wie beim Zeichnen - **eine Zählung, die anders rechnet als die
 * Zeichnung, deckelt das Falsche**, und zwar um bis zu einen Punkt je Ring.
 *
 * Ein `MultiPolygon` bringt mehrere Umringe mit, ein Polygon mit Loch
 * ebenfalls: beide zählen ganz, denn beide werden ganz gezeichnet.
 */
export function grenzpunkteZaehlen(stuecke: readonly MitUmring[]): number {
  let n = 0;
  for (const f of stuecke ?? []) {
    const g = f?.geometry;
    const ringe: any[] = [];
    if (g?.type === 'Polygon') ringe.push(...(g.coordinates ?? []));
    else if (g?.type === 'MultiPolygon') {
      for (const teil of g.coordinates ?? []) ringe.push(...(teil ?? []));
    }
    for (const r of ringe) {
      if (!Array.isArray(r) || !r.length) continue;
      const zu = r.length > 1
        && r[0]?.[0] === r[r.length - 1]?.[0]
        && r[0]?.[1] === r[r.length - 1]?.[1];
      n += zu ? r.length - 1 : r.length;
    }
  }
  return n;
}

/**
 * Werden die Grenzpunkte auf diesem Ausschnitt gezeichnet?
 *
 * Zwei Deckel, jeder mit seinem Grund - und die Antwort steht EINMAL, damit
 * die Karte und der Satz darunter nicht auseinanderlaufen können.
 */
export function grenzpunkteZeigen(zoom: number, punkte: number): boolean {
  return punkte > 0
    && punkte <= GRENZPUNKTE_BIS_PUNKTE
    && zoom >= GRENZPUNKTE_AB_STUFE;
}

/**
 * Und was unter der Karte darüber steht.
 *
 * **WAS DER DECKEL WEGNIMMT, SAGT ER.** Eine Karte ohne Grenzpunkte sieht
 * aus wie eine, deren Zeichnung nicht geht - genau das war die Beanstandung,
 * und der Grund dafür stand nirgends. Der Satz nennt deshalb nicht nur die
 * Zahl der Flurstücke, sondern auch, warum die Punkte fehlen und was man
 * dagegen tut.
 */
export function grenzpunktSatz(
  stuecke: number, zoom: number, punkte: number
): string {
  if (!stuecke) return '';
  const kopf = `${stuecke} Flurstücke`;
  if (zoom < GRENZPUNKTE_AB_STUFE) {
    return `${kopf}, Grenzpunkte ab Stufe ${GRENZPUNKTE_AB_STUFE}`;
  }
  if (punkte > GRENZPUNKTE_BIS_PUNKTE) {
    return `${kopf}, ${punkte} Grenzpunkte - zu dicht, näher heranzoomen`;
  }
  return `${kopf}, ${punkte} Grenzpunkte`;
}
